import {
  Group,
  Text,
  Box,
  Badge,
  Progress,
  Tooltip,
} from '@mantine/core';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
} from 'react-feather';
import type { Participant } from '../../../../types';
import type { ColumnConfig } from '../../../../hooks';
import { ParticipantAvatar, GroupBadge, renderTextCell, AttendanceRate } from '../../../../components/common';
import { formatDate } from '../../../../utils/formatters';
import { bucketAsAttendanceFields } from '../../../../utils/attendanceModalityLabels';
import classes from '../ParticipantsTab.module.css';

/** Map raw email_type values to human-readable labels */
const EMAIL_TYPE_LABELS: Record<string, string> = {
  invite: 'Invitation',
  invitation: 'Invitation',
  invitation_email: 'Invitation',
  welcome: 'Welcome',
  password_reset: 'Password Reset',
  reminder: 'Reminder',
  notification: 'Notification',
  verification: 'Verification',
  export_ready: 'Export Ready',
  system: 'System',
};

function formatEmailTypeLabel(emailType: string): string {
  return EMAIL_TYPE_LABELS[emailType] || emailType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Re-export for existing consumers
export { renderTextCell };

/** Render a participant status badge */
export function renderStatus(status: Participant['status']) {
  const config = {
    pending: { color: 'yellow', label: 'Pending' },
    active: { color: 'medleyGreen', label: 'Active' },
    withdrawn: { color: 'gray', label: 'Withdrawn' },
  };
  const { color, label } = config[status];
  // MED-1367: status pills are uppercase with 0.3px letter-spacing
  // (docs/STYLE_GUIDE.md, Status Pills).
  return (
    <Badge
      color={color}
      variant="light"
      size="sm"
      tt="uppercase"
      styles={{ label: { letterSpacing: '0.3px' } }}
    >
      {label}
    </Badge>
  );
}

/** Render an intake status badge */
export function renderIntakeStatus(participant: Participant) {
  if (participant.intake_completed_at) {
    return (
      <Badge color="medleyGreen" variant="light" size="sm" tt="none" leftSection={<CheckCircle size={10} />}>
        Completed
      </Badge>
    );
  }
  if (participant.intake_form_sent_at) {
    return (
      <Badge color="warningAmber" variant="light" size="sm" tt="none" leftSection={<Clock size={10} />}>
        Pending
      </Badge>
    );
  }
  return (
    <Badge variant="light" color="gray" size="sm" tt="none" styles={{ root: { border: 'none', color: '#888888' } }}>
      Not Sent
    </Badge>
  );
}

/** Render a group badge */
export function renderGroupBadge(participant: Participant) {
  return (
    <GroupBadge
      groupName={participant.group_name}
      groupId={participant.group_id}
    />
  );
}

/** Render health indicator dot and label */
function renderHealth(participant: Participant) {
  const pct = participant.attendance_percentage;
  // MED-394: No attendance data → "No Data" instead of dash
  if (pct === null || pct === undefined) {
    return (
      <Group gap={6} wrap="nowrap">
        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc', display: 'inline-block', flexShrink: 0 }} />
        <Text fz={12} c="dimmed" fw={500}>No Data</Text>
      </Group>
    );
  }
  const dotColor = pct < 70 ? '#D64545' : pct < 80 ? '#E9A23B' : '#4A7C59';
  const label = pct < 70 ? 'At Risk' : pct < 80 ? 'Monitor' : 'Healthy';
  return (
    <Group gap={6} wrap="nowrap">
      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, display: 'inline-block', flexShrink: 0 }} />
      <Text fz={12} c={dotColor} fw={500}>{label}</Text>
    </Group>
  );
}

/** Fields the attendance and sessions cells read. */
type AttendanceFields = Pick<
  Participant,
  'attendance_percentage' | 'sessions_attended' | 'total_sessions' | 'attendance_population'
>;

/** Render attendance progress bar. Exported for the coach view (MED-1367). */
export function renderAttendance(participant: AttendanceFields) {
  if (participant.attendance_percentage !== null) {
    return (
      <Group gap="sm" wrap="nowrap">
        <Progress
          value={participant.attendance_percentage}
          size="sm"
          w={80}
          color={
            participant.attendance_percentage >= 80
              ? 'medleyGreen'
              : participant.attendance_percentage >= 50
                ? 'yellow'
                : 'red'
          }
        />
        {/* MED-1266: rate + marking coverage via the shared component (total_sessions = marked). */}
        <AttendanceRate
          rate={participant.attendance_percentage}
          marked={participant.total_sessions}
          population={participant.attendance_population}
          variant="compact"
          size="13px"
        />
      </Group>
    );
  }
  return <Text fz={13} c="dimmed">&mdash;</Text>;
}

/** Render attended / recorded sessions. Exported for the coach view (MED-1367). */
export function renderSessions(participant: AttendanceFields) {
  return (
    <Text fz={13}>
      {participant.sessions_attended}/{participant.total_sessions}
    </Text>
  );
}

/** Format a withdrawal date for the WITHDRAWN pill */
function formatWithdrawnDate(withdrawnAt: string | null): string {
  if (!withdrawnAt) return 'WITHDRAWN';
  const date = new Date(withdrawnAt);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const currentYear = new Date().getFullYear();
  const withdrawnYear = date.getFullYear();
  if (withdrawnYear !== currentYear) {
    return `WITHDRAWN ${month}/${day}/${String(withdrawnYear).slice(-2)}`;
  }
  return `WITHDRAWN ${month}/${day}`;
}

/** Render the WITHDRAWN pill badge */
export function WithdrawnPill({ withdrawnAt }: { withdrawnAt: string | null }) {
  return (
    <Badge
      size="xs"
      variant="filled"
      styles={{
        root: {
          backgroundColor: '#E8E8E8',
          color: '#444444',
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          fontWeight: 600,
        },
      }}
    >
      {formatWithdrawnDate(withdrawnAt)}
    </Badge>
  );
}

/** Render survey completion status (midpoint/endpoint) */
function renderSurveyCompletion(completed: boolean) {
  if (completed) {
    return (
      <Badge color="medleyGreen" variant="light" size="sm" tt="none" leftSection={<CheckCircle size={10} />}>
        Complete
      </Badge>
    );
  }
  return (
    <Badge variant="light" color="gray" size="sm" tt="none" styles={{ root: { border: 'none', color: '#888888' } }}>
      Incomplete
    </Badge>
  );
}

/** Render participant name cell with avatar and flag badge */
function renderNameCell(participant: Participant) {
  const isWithdrawn = participant.status === 'withdrawn';
  return (
    <Group gap="sm" wrap="nowrap">
      <ParticipantAvatar
        firstName={participant.first_name}
        lastName={participant.last_name}
        size={40}
        style={isWithdrawn ? { opacity: 0.5 } : undefined}
      />
      <Box>
        <Group gap="xs" wrap="nowrap">
          <Text fw={500} fz={13} className={classes.participantName}>
            {participant.first_name} {participant.last_name}
          </Text>
          {isWithdrawn && (
            <WithdrawnPill withdrawnAt={participant.withdrawn_at} />
          )}
          {!isWithdrawn && participant.has_open_flag && (
            <Tooltip
              label={participant.flag_reason || 'Flagged participant'}
              maw={300}
              multiline
              withinPortal
              events={{ hover: true, focus: true, touch: true }}
            >
              <span style={{ display: 'inline-flex' }}>
                <Badge size="xs" color="warningAmber" variant="light" leftSection={<AlertTriangle size={10} />}>
                  Flagged{participant.flag_session_number ? ` - S${participant.flag_session_number}` : ''}
                </Badge>
              </span>
            </Tooltip>
          )}
        </Group>
        <Text fz={11} c="dimmed" className={classes.participantEmail}>
          {participant.email}
        </Text>
      </Box>
    </Group>
  );
}

/** Base form URLs for feedback surveys, keyed by survey type */
export interface FormBaseUrls {
  midpoint: string | null;
  endpoint: string | null;
  post_session: string | null;
}

/** Build a participant-specific form URL from a base URL */
function buildParticipantFormUrl(baseUrl: string | null, participantId: string): string | null {
  if (!baseUrl) return null;
  const url = new URL(baseUrl);
  url.searchParams.set('sgos_participant_id', participantId);
  return url.toString();
}

/** Render a form URL link cell */
function renderFormUrlCell(url: string | null) {
  if (!url) {
    return <Text fz={13} c="dimmed">&mdash;</Text>;
  }
  return (
    <Tooltip label={url} position="top-start" multiline maw={400}>
      <Text
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        fz={13}
        c="blue"
        td="underline"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        Open Form
      </Text>
    </Tooltip>
  );
}

/** Main cell content renderer - dispatches to specific renderers based on column id */
export function renderCellContent(column: ColumnConfig, participant: Participant, formBaseUrls?: FormBaseUrls) {
  switch (column.id) {
    case 'name':
      return renderNameCell(participant);
    case 'email':
      return (
        <Box>
          <Text fz={13}>{participant.email}</Text>
          {participant.latest_email_type && (
            <Text fz={11} c="dimmed">
              {formatEmailTypeLabel(participant.latest_email_type)} &middot; {formatDate(participant.latest_email_sent_at)}
            </Text>
          )}
        </Box>
      );
    case 'role':
      return renderTextCell(participant.role);
    case 'business_unit':
      return renderTextCell(participant.business_unit);
    case 'location':
      return renderTextCell(participant.location);
    case 'personality_type':
      return participant.personality_type ? (
        <Badge variant="light" color="gray" size="sm" tt="lowercase">
          {participant.personality_type}
        </Badge>
      ) : (
        <Text fz={13} c="dimmed">&mdash;</Text>
      );
    case 'group':
      return renderGroupBadge(participant);
    case 'intake_status':
      return renderIntakeStatus(participant);
    case 'intake_form_url':
      return renderFormUrlCell(participant.intake_form_url ?? null);
    case 'midpoint_form_url':
      return renderFormUrlCell(buildParticipantFormUrl(formBaseUrls?.midpoint ?? null, participant.id));
    case 'endpoint_form_url':
      return renderFormUrlCell(buildParticipantFormUrl(formBaseUrls?.endpoint ?? null, participant.id));
    case 'post_session_form_url':
      return renderFormUrlCell(buildParticipantFormUrl(formBaseUrls?.post_session ?? null, participant.id));
    case 'experience':
      return renderTextCell(participant.experience);
    case 'primary_goal':
      return renderTextCell(participant.primary_goal, 150);
    case 'intake_timezone':
      return renderTextCell(participant.intake_timezone);
    case 'health':
      return renderHealth(participant);
    case 'attendance':
      return renderAttendance(participant);
    case 'sessions':
      return renderSessions(participant);
    case 'attendance_one_on_one':
      return renderAttendance(bucketAsAttendanceFields(participant.attendance_by_modality?.one_on_one));
    case 'attendance_workshop':
      return renderAttendance(bucketAsAttendanceFields(participant.attendance_by_modality?.workshop));
    case 'midpoint_completed':
    case 'endpoint_completed':
      return renderSurveyCompletion(participant[column.id]);
    case 'status':
      return renderStatus(participant.status);
    default:
      // Handle custom fields
      if (column.isCustomField) {
        const value = participant.custom_fields?.[column.id];
        return renderTextCell(value);
      }
      return <Text fz={13}>&mdash;</Text>;
  }
}
